import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

const LAB_LOGO_STORAGE_KEY = 'labLogoUrl';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const persistAuth = (newToken, newUser) => {
    if (newToken) localStorage.setItem('token', newToken);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
      if (newUser.labLogoUrl) localStorage.setItem(LAB_LOGO_STORAGE_KEY, newUser.labLogoUrl);
    }
    setToken(newToken || null);
    setUser(newUser || null);
  };

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await authAPI.getCurrentUser();
        if (data?.user) {
          setUser(data.user);
          setToken(savedToken);
          localStorage.setItem('user', JSON.stringify(data.user));
          if (data.user.labLogoUrl) {
            localStorage.setItem(LAB_LOGO_STORAGE_KEY, data.user.labLogoUrl);
          }
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await authAPI.login({ email, password });
      persistAuth(data.token, data.user);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Invalid credentials';
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await authAPI.register(userData);
      persistAuth(data.token, data.user);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAB_LOGO_STORAGE_KEY);
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const { data } = await authAPI.getCurrentUser();
    if (data?.user) {
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user.labLogoUrl) {
        localStorage.setItem(LAB_LOGO_STORAGE_KEY, data.user.labLogoUrl);
      }
    }
    return data?.user;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
